import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { useInitialCampaignsSettled } from './table-startup';
import type {
  CabinetsOverview,
  CabinetRef,
  ConnectResult,
  ПроверкаКлюча,
  CampaignRef,
  Cluster,
  DataHealth,
  Overview,
  ProductRef,
  Version,
  Workspace,
  WorkspacesReply,
  Breakdown,
  ПолкиОтвет,
} from './types';
import type { Period } from '../pages/period';
import { planHint, подписьТарифа } from '../billing/hint';

/**
 * Запросы каркаса.
 *
 * Ключи собираются здесь, а не по месту вызова. Ключ, набранный руками
 * в двух экранах, однажды разойдётся на один символ — и два экрана начнут
 * держать два разных кеша одного и того же, показывая разные числа рядом.
 */
export const keys = {
  version: ['version'] as const,
  workspaces: ['workspaces'] as const,
  cabinets: (wsId: string) => ['cabinets', wsId] as const,
  cabinetsOverview: (wsId: string) => ['cabinets-overview', wsId] as const,
  stop: (wsId: string) => ['stop', wsId] as const,
  dataHealth: (wsId: string, cabinetId: string) => ['data-health', wsId, cabinetId] as const,
  /**
   * Ключ периода — строкой запроса, а не числом суток.
   *
   * ПОТОМУ ЧТО ПЕРИОД БОЛЬШЕ НЕ ЧИСЛО. «Последние 7 суток» и «с 1 по 7
   * августа» — разные данные; сведи их к числу 7, и кеш отдал бы одно
   * вместо другого, причём молча.
   */
  campaigns: (ws: string, cab: string, query: string) => ['campaigns', ws, cab, query] as const,
  overview: (ws: string, cab: string, query: string) => ['overview', ws, cab, query] as const,
  products: (ws: string, cab: string, campaignId: string) =>
    ['products', ws, cab, campaignId] as const,
  /* ОКНО ВХОДИТ В КЛЮЧ ОТДЕЛЬНОЙ ЧАСТЬЮ: переход с «вчера» на «сегодня»
     не меняет числа дней (и там и там одни сутки), и без окна в ключе
     кабинет отдал бы кэш соседнего дня. */
  clusters: (ws: string, cab: string, campaign: string, nm: number, days: number, окно = '') =>
    ['clusters', ws, cab, campaign, nm, days, окно] as const,
};

interface Where {
  wsId: string;
  cabinetId: string;
}

const base = (w: Where): string => `/workspaces/${w.wsId}/cabinets/${w.cabinetId}`;

export function useVersion() {
  return useQuery({
    queryKey: keys.version,
    queryFn: () => api<Version>('/version'),
    // Сборка не меняется, пока страницу не перезагрузили: перезапрашивать
    // её при каждом возврате в окно — шум без пользы.
    staleTime: Infinity,
  });
}

/**
 * Список пространств и наблюдение — ОДИН запрос на два вопроса.
 *
 * `select` разбирает общий ответ на две части; кэш при этом один,
 * и в сеть уходит один запрос, сколько бы мест ни спрашивало.
 * Разъедься эти ответы на два запроса — и кабинет успевал бы
 * показать чужие числа раньше полосы «вы смотрите чужое».
 */
const workspacesQuery = {
  queryKey: keys.workspaces,
  queryFn: () => api<WorkspacesReply>('/workspaces'),
};

export function useWorkspaces() {
  return useQuery({ ...workspacesQuery, select: (d: WorkspacesReply) => d.workspaces });
}

/** Что сотрудник смотрит под наблюдением прямо сейчас (FR-22). */
export function useWatch() {
  return useQuery({ ...workspacesQuery, select: (d: WorkspacesReply) => d.watching });
}

export function useCabinets(wsId: string | undefined) {
  return useQuery({
    queryKey: keys.cabinets(wsId ?? ''),
    queryFn: async () =>
      (await api<{ cabinets: CabinetRef[] }>(`/workspaces/${wsId}/cabinets`)).cabinets,
    enabled: Boolean(wsId),
  });
}

/**
 * Подключить кабинет ключом продавца.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * СПИСОК КАБИНЕТОВ ПЕРЕЧИТЫВАЕТСЯ НЕ ЗДЕСЬ, А ПО КНОПКЕ «ОТКРЫТЬ».
 *
 * Соблазн — обновить список сразу по успеху. Но экран подключения
 * показывается ровно потому, что кабинетов ноль; обновлённый список
 * тут же уводит человека в кабинет — мимо ответа, ради которого
 * он сюда пришёл. А в ответе сказано, каких прав у ключа не хватает
 * и что из-за этого не будет работать.
 *
 * Эти строки — единственный момент, когда человек ещё помнит, где
 * выпускал ключ. Промелькнув, они превращаются в обращение
 * «у меня пустая таблица» через неделю.
 * ═══════════════════════════════════════════════════════════════════════
 */
/**
 * ═══════════════════════════════════════════════════════════════════════
 * ПРОВЕРИТЬ КЛЮЧ, НИЧЕГО НЕ ПОДКЛЮЧАЯ.
 *
 * Отдельно от `useConnectCabinet`, и это не дублирование. Подключение
 * заводит кабинет в реестре и тратит его пробный период (решение Р-2);
 * проверка — вопрос, а не действие, и человек нажимает её, ещё
 * не решив подключать.
 *
 * Ответ — четыре строки эталона: что уже хорошо, что именно плохо
 * и что ещё не проверяли. `connect` бросает исключение на первой же
 * беде и о второй молчит.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function useCheckToken(wsId: string | undefined) {
  return useMutation({
    mutationFn: (token: string) =>
      api<ПроверкаКлюча>(`/workspaces/${wsId}/cabinets/check`, {
        method: 'POST',
        body: { token },
      }),
  });
}

export function useConnectCabinet(wsId: string | undefined) {
  return useMutation({
    mutationFn: (token: string) =>
      api<{ ok: true; cabinet: ConnectResult }>(`/workspaces/${wsId}/cabinets`, {
        method: 'POST',
        body: { token },
      }).then((r) => r.cabinet),
  });
}

/** Перечитать список кабинетов — после того, как ответ прочитан. */
export function useRefreshCabinets(wsId: string | undefined) {
  const qc = useQueryClient();

  return () => {
    void qc.invalidateQueries({ queryKey: keys.cabinets(wsId ?? '') });
    void qc.invalidateQueries({ queryKey: keys.cabinetsOverview(wsId ?? '') });
    void qc.invalidateQueries({ queryKey: keys.workspaces });
  };
}

/**
 * Раздел «Кабинеты»: роли, состояние ключей, место в тарифе.
 *
 * ОТДЕЛЬНО ОТ `useCabinets`, И ЭТО НЕ ДУБЛИРОВАНИЕ. Тот список кормит
 * переключатель наверху и запрашивается на каждом экране; здесь на каждый
 * кабинет читается состояние ключа и считается расход пространства.
 * Слепи их — и цена этого расчёта появится на показе таблицы кампаний.
 */
export function useCabinetsOverview(wsId: string | undefined,
  priority: { afterTable?: string; immediate?: boolean } = {},
) {
  const tableSettled = useInitialCampaignsSettled(wsId, priority.afterTable, priority.immediate);
  return useQuery({
    queryKey: keys.cabinetsOverview(wsId ?? ''),
    queryFn: () => api<CabinetsOverview>(`/workspaces/${wsId}/cabinets/overview`),
    enabled: Boolean(wsId) && tableSettled,
  });
}

/**
 * Аварийный рубильник пространства (FR-10.3).
 *
 * ═══════════════════════════════════════════════════════════════════════
 * СПРАШИВАЕТСЯ ОТДЕЛЬНО ОТ ВСЕГО ОСТАЛЬНОГО, И ЭТО НАРОЧНО.
 *
 * Состояние рубильника — единственное, что обязано быть верным даже
 * тогда, когда остальные запросы отказали: полоса «управление
 * остановлено» не должна пропадать оттого, что не прочитались кабинеты.
 * ═══════════════════════════════════════════════════════════════════════
 */
export interface StopState {
  stopped: boolean;
  at: string | null;
  by: string | null;
  reason: string | null;
  text: string | null;
  mayResume: boolean;
}

export function useStopState(wsId: string | undefined) {
  return useQuery({
    queryKey: keys.stop(wsId ?? ''),
    queryFn: () => api<StopState>(`/workspaces/${wsId}/stop`),
    enabled: Boolean(wsId),
  });
}

export function useStopNow(wsId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (reason: string | null) =>
      api<StopState>(`/workspaces/${wsId}/stop`, { method: 'POST', body: { reason } }),
    // ВСЁ ПОДРЯД, А НЕ ОДИН КЛЮЧ: рубильник меняет ответ на любой вопрос
    // про управление — от полосы над экраном до кнопок в кампании
    onSuccess: () => void qc.invalidateQueries(),
  });
}

export function useResume(wsId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () => api<StopState>(`/workspaces/${wsId}/stop`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries(),
  });
}

/**
 * Отказаться от доступа к чужому кабинету.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ТОЛЬКО ДЛЯ ВЫДАННЫХ. Свой кабинет так не отвязать: сервер откажет
 * словами «нельзя отозвать доступ у владельца кабинета».
 *
 * КАМПАНИИ ПРИ ЭТОМ НЕ ОСТАНАВЛИВАЮТСЯ — они продолжают работать,
 * просто без нас. Сервер говорит это в ответе, и экран показывает
 * его ответ, а не свою догадку.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function useLeaveCabinet(wsId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (cabinetId: string) =>
      api<{ ok: true; message: string }>(
        `/workspaces/${wsId}/cabinets/${cabinetId}/access/${wsId}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.cabinets(wsId ?? '') });
      void qc.invalidateQueries({ queryKey: keys.cabinetsOverview(wsId ?? '') });
    },
  });
}

/**
 * Отвязать СВОЙ кабинет целиком.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ЭТО НЕ ТО ЖЕ, ЧТО ОТКАЗ ОТ ДОСТУПА, ХОТЯ АДРЕС ПОХОЖ.
 *
 * Отказ от доступа — про чужой кабинет: мы уходим, всё остальное
 * продолжает работать. Отвязка — про свой: ключ гасится, обращения
 * к Wildberries прекращаются, управление кампаниями снимается.
 * Кампании в самом Wildberries при этом остаются как есть.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function useUnlinkCabinet(wsId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (cabinetId: string) =>
      api<{ ok: true; stopped: number; tokens: number; message: string }>(
        `/workspaces/${wsId}/cabinets/${cabinetId}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.cabinets(wsId ?? '') });
      void qc.invalidateQueries({ queryKey: keys.cabinetsOverview(wsId ?? '') });
      void qc.invalidateQueries({ queryKey: keys.workspaces });
    },
  });
}

/** Проверить ключ кабинета прямо сейчас — не дожидаясь суточной проверки. */
export function useVerifyCabinet(wsId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (cabinetId: string) =>
      api<{ ok: boolean; scopes: string[]; error: string | null }>(
        `/workspaces/${wsId}/cabinets/${cabinetId}/verify`,
        { method: 'POST', body: {} },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.cabinetsOverview(wsId ?? '') });
    },
  });
}

/**
 * Достоверность данных — на весь кабинет один запрос.
 *
 * Обновляется сама раз в минуту: плашка «данные на 14:20» обязана
 * стареть на глазах, иначе она превращается в украшение. Обновление
 * фоновое, поэтому экран при этом не мигает.
 */
export function useDataHealth(wsId: string | undefined, cabinetId: string | undefined) {
  return useQuery({
    queryKey: keys.dataHealth(wsId ?? '', cabinetId ?? ''),
    queryFn: () => api<DataHealth>(`/workspaces/${wsId}/cabinets/${cabinetId}/data-health`),
    enabled: Boolean(wsId && cabinetId),
    refetchInterval: 60_000,
  });
}

/**
 * Строка запроса периода.
 *
 * ЧИСЛО ТОЖЕ ПРИНИМАЕТСЯ — ради экранов, которым период не выбирают
 * (кластеры, правила, карточка кампании). Убери его, и пришлось бы
 * заводить выбор периода там, где он никому не нужен.
 */
/**
 * Строка периода для запроса.
 *
 * ВЫНЕСЕНА НАРУЖУ, ЧТОБЫ ВЫГРУЗКА В EXCEL СПРАШИВАЛА ТОТ ЖЕ ПЕРИОД,
 * ЧТО И СПИСОК. Собранная второй раз в другом месте, она однажды
 * разойдётся со списком, и в файле окажется не то, что на экране.
 */
export function periodQuery(p: number | Period): string {
  return typeof p === 'number' ? `days=${p}` : `from=${p.from}&to=${p.to}`;
}

/**
 * Через сколько переспрашивать пустой список кампаний.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ПУСТОЙ СПИСОК СРАЗУ ПОСЛЕ ПОДКЛЮЧЕНИЯ — ЭТО НЕ «ПУСТО», А «ЕЩЁ ИДЁТ».
 *
 * Подключение кабинета запускает первую выгрузку и не ждёт её:
 * четыре обращения к Wildberries подряд занимают у большого кабинета
 * минуту с лишним. Человек в это время уже стоит на экране кампаний
 * и видит «кампаний нет». Заставить его нажимать «Синхронизировать»
 * ради того, что и так происходит, — плохой способ поздороваться.
 *
 * ПЕРЕСПРАШИВАЕМ ТОЛЬКО ПУСТОЙ. Список, в котором что-то есть,
 * не дёргаем: он и так обновляется по расписанию и по кнопке,
 * а лишний запрос в минуту с каждой открытой вкладки — это нагрузка
 * ни за чем.
 * ═══════════════════════════════════════════════════════════════════════
 */
export const EMPTY_RETRY_MS = 15_000;

export function useCampaigns(w: Where, period: number | Period = 30) {
  const q = periodQuery(period);
  return useQuery({
    queryKey: keys.campaigns(w.wsId, w.cabinetId, q),
    queryFn: async () =>
      (await api<{ campaigns: CampaignRef[] }>(`${base(w)}/campaigns?${q}`)).campaigns,
    refetchInterval: (query) =>
      (query.state.data?.length ?? 0) === 0 ? EMPTY_RETRY_MS : false,
  });
}

/**
 * Плитки над списком.
 *
 * ЗАПРОС ОТДЕЛЬНЫЙ ОТ СПИСКА, И ЭТО НАМЕРЕННО. Плитки считают
 * и завершённые кампании, которых в списке нет вовсе: сложить их
 * в браузере из того, что пришло в списке, значило бы показать расход
 * меньше настоящего — ровно на те кампании, которые человек закрыл
 * и забыл. Период при этом один и тот же, и он приходит с сервера.
 */
export function useOverview(w: Where, period: number | Period = 30) {
  const q = periodQuery(period);
  return useQuery({
    queryKey: keys.overview(w.wsId, w.cabinetId, q),
    queryFn: () => api<Overview>(`${base(w)}/overview?${q}`),
    enabled: Boolean(w.wsId && w.cabinetId),
  });
}

export function useCampaignProducts(w: Where, campaignId: string | undefined) {
  return useQuery({
    queryKey: keys.products(w.wsId, w.cabinetId, campaignId ?? ''),
    queryFn: async () =>
      (await api<{ products: ProductRef[] }>(`${base(w)}/campaigns/${campaignId}/products`))
        .products,
    enabled: Boolean(campaignId),
  });
}

/**
 * Кластеры выбранной пары «кампания и товар».
 *
 * ЗАПРАШИВАЮТСЯ ЦЕЛИКОМ, а фильтруются и сортируются в браузере (13.3).
 * Единица опроса Wildberries — пара, и на живом кабинете это сотни строк,
 * редко больше тысячи. Серверная фильтрация добавляла бы задержку сети
 * на каждое нажатие клавиши в поиске — то есть меняла бы мгновенный
 * отклик на ожидание ради экономии, которой здесь не на чем взяться.
 *
 * Где это перестанет работать: сводный экран по всем кампаниям кабинета,
 * там десятки тысяч строк. Он делается серверной постраничной выборкой
 * и это отдельная задача, а не переделка этой таблицы.
 */
/**
 * ═══════════════════════════════════════════════════════════════════════
 * СПРОСИТЬ У WILDBERRIES КЛАСТЕРЫ ЭТОЙ КАМПАНИИ ЗАНОВО.
 *
 * 01.09.2026 владелец: «в новой кампании отображается 1 кластер уже час
 * точно, а в кабинете ВБ уже насобиралось кучу… клиент его не будет
 * видеть, а пока он не видит, кластер сливает бюджет».
 *
 * ЗОВЁТСЯ ПРИ ОТКРЫТИИ ВКЛАДКИ, А НЕ КНОПКОЙ. Кнопка «спросить заново»
 * рядом с таблицей означала бы, что человек должен ЗНАТЬ, что таблица
 * бывает старой, и помнить нажимать. Он смотрит сюда — значит числа
 * должны быть свежими; это не его работа.
 *
 * ЗАСЛОНКА — НА СЕРВЕРЕ, минута на кампанию: вкладку переключают
 * туда-обратно, и без неё каждое движение стоило бы квоты продавца.
 * Отказ ничего не ломает: таблица покажет то, что уже лежит в базе.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function useОбновитьКластеры(w: Where, campaignId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      api<{ ok: boolean; skipped?: boolean; seen?: number }>(
        `${base(w)}/campaigns/${campaignId}/clusters/refresh`,
        { method: 'POST', body: {} },
      ),
    onSuccess: async (r) => {
      /* ПЕРЕЧИТЫВАЕМ ТОЛЬКО ЕСЛИ ЧТО-ТО СПРАШИВАЛИ. Заслонка ответила
         «спрашивали только что» — в базе ничего не изменилось, и гасить
         ключ значило бы устроить лишний поход за теми же строками. */
      if (r.ok && r.skipped !== true) {
        // Синхронизация меняет и sync_runs. Полоса достоверности должна
        // перечитаться вместе со строками, не дожидаясь минутного опроса.
        await Promise.all([
          qc.invalidateQueries({ queryKey: ['clusters'] }),
          qc.invalidateQueries({ queryKey: keys.dataHealth(w.wsId, w.cabinetId) }),
        ]);
      }
    },
    /* ОТКАЗ МОЛЧА. Это фоновое освежение, а не действие человека:
       он открыл вкладку, а не нажал кнопку. Красная плашка про то,
       чего он не просил, только пугает. */
    onError: () => {},
  });
}

export function useClusters(
  w: Where,
  campaignId: string | undefined,
  nmId: number | undefined,
  days: number,
  /* ГРАНИЦЫ ОКНА, если человек выбрал их календарём. Довод, почему
     их не заменить числом дней, — у разбора отбора: «вчера» это один
     закончившийся день, а `days=1` — последние сутки, считая сегодня. */
  window?: { from: string; to: string },
) {
  const окно = window === undefined ? '' : `&from=${window.from}&to=${window.to}`;
  return useQuery({
    queryKey: keys.clusters(w.wsId, w.cabinetId, campaignId ?? '', nmId ?? 0, days, окно),
    queryFn: async () => {
      // Товара может не быть, и это НОРМАЛЬНЫЙ случай: кампания уже задаёт
      // свои товары, отдельно их не выбирают. Подставь мы `undefined`
      // в адрес — сервер получил бы `nm=undefined` и ответил отказом.
      const nm = nmId === undefined ? '' : `&nm=${nmId}`;
      const r = await api<{ clusters: Cluster[] }>(
        `${base(w)}/clusters?campaign=${campaignId}${nm}&days=${days}${окно}`,
      );
      return r.clusters;
    },
    // Товар в условие НЕ входит: без него запрос отдаёт кластеры всей
    // кампании. Прежнее условие выключало запрос совсем, и таблица
    // молча показывала ноль — ошибка, которую видно только глазами.
    enabled: Boolean(campaignId),
  });
}

/**
 * Разбор кампании по товарам.
 *
 * СПРАШИВАЕТСЯ ТОЛЬКО КОГДА РАСКРЫЛИ. Сорок кампаний по восемь товаров —
 * это триста строк, которых человек не просил: он открыл список, чтобы
 * увидеть кампании, а разбор нужен ему у одной-двух.
 *
 * ПЕРИОД ТОТ ЖЕ, ЧТО У СПИСКА. Разбор за другой промежуток, чем строка
 * над ним, — два разных ответа на один вопрос.
 */
export function useBreakdown(w: Where, campaignId: string, period: number | Period, open: boolean) {
  const q = periodQuery(period);
  return useQuery({
    queryKey: ['breakdown', w.wsId, w.cabinetId, campaignId, q],
    enabled: open,
    queryFn: async () => api<Breakdown>(`${base(w)}/campaigns/${campaignId}/breakdown?${q}`),
  });
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * РЕКОМЕНДАТЕЛЬНЫЕ ПОЛКИ ПО СУТКАМ — вкладка «Рекомендации»
 * (передача 15.09.2026, П-3).
 *
 * СПРАШИВАЕТСЯ ТОЛЬКО КОГДА ВКЛАДКУ ОТКРЫЛИ. Запрос тянет два отчёта
 * за семь суток и складывает их по дням; у человека, зашедшего
 * в «Деньги и товары», спрашивать это незачем.
 *
 * СЕМЬ СУТОК, А НЕ ПЕРИОД ЭКРАНА. Панель так и подписана — «за 7 суток»,
 * и светофор в ней считается от среднего ЗА ЭТИ ЖЕ семь. Возьми период
 * сверху — и подпись разошлась бы с числами.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function usePolki(w: Where, campaignId: string, open: boolean) {
  return useQuery({
    queryKey: ['polki', w.wsId, w.cabinetId, campaignId],
    enabled: open,
    queryFn: async () => api<ПолкиОтвет>(`${base(w)}/campaigns/${campaignId}/polki?days=7`),
  });
}

/**
 * Срок подписки — для подписи в ящике.
 *
 * ОТКАЗ НЕ ПОКАЗЫВАЕТСЯ НИЧЕМ И НЕ ПОВТОРЯЕТСЯ. Подпись под пунктом
 * меню — не то сведение, ради которого стоит ругаться на экране
 * или ходить в сеть заново.
 */
/**
 * Строка тарифа для меню разделов: «Рабочий · до 14.09.2026».
 *
 * Тот же запрос, что у `useSubscriptionHint` — тот же ключ, значит
 * один поход на сервер на оба места.
 */
/**
 * Потолки тарифа: сколько кабинетов и кампаний он разрешает.
 *
 * Тот же запрос и тот же ключ, что у подписи и у капсулы остатка, —
 * значит один поход на сервер на три места. Потолок `null` означает
 * «без ограничения», и звать его нулём нельзя: ноль — это запрет.
 */
export function usePlanLimits(wsId: string | undefined): {
  cabinets: number | null;
  campaigns: number | null;
  /**
   * Сколько занято — СЧИТАЕТ СЛУЖБА, А НЕ ЭКРАН.
   *
   * Потолок один на пространство, а экран видит один кабинет из пяти
   * и только те кампании, что пришли в список. Довод целиком —
   * у `usage` в `workspace.controller.ts`.
   */
  занятоКабинетов: number | null;
  занятоКампаний: number | null;
  /**
   * Идущие опыты и потолки по ним — передача 07.09.2026, раздел 3.
   *
   * `предел: null` означает «тариф про это молчит»: пара в полосе итогов
   * не показывается, запуск не запрещается. Ноль здесь был бы запретом,
   * а молчание тарифа — не запрет.
   */
  тесты: {
    ставки: { занято: number; предел: number | null };
    фото: { занято: number; предел: number | null };
  } | null;
} | null {
  const q = useQuery({
    queryKey: ['billing', wsId],
    queryFn: () =>
      api<{
        current: {
          limits: Record<string, number | null | undefined>;
          usage?: Record<string, number | null | undefined> | null;
          tests?: {
            ставки: { занято: number; предел: number | null };
            фото: { занято: number; предел: number | null };
          } | null;
        } | null;
      }>(`/workspaces/${wsId}/billing`),
    enabled: Boolean(wsId),
    retry: false,
    staleTime: 5 * 60_000,
  });

  if (q.error || q.data === undefined || q.data.current === null) return null;
  const потолки = q.data.current.limits;
  /**
   * ═══════════════════════════════════════════════════════════════════
   * КЛЮЧ ПОТОЛКА КАМПАНИЙ — `managed_campaigns`, И ЭТО НЕ ПРИДИРКА.
   *
   * Служба кладёт в тариф ровно эти ключи (`src/billing/plans.seed.ts`:
   * `{ cabinets, managed_campaigns }`) и отдаёт объект КАК ЕСТЬ
   * (`workspace.controller.ts`: `limits: current.limits`). Здесь же
   * читалось `limits.campaigns` — ключа с таким именем не приходило
   * НИКОГДА, и плашка «Кампании N из M» на живом кабинете оставалась
   * без предела: `undefined` не равен `null`, проверка на «без
   * ограничения» не срабатывала, и в строку уходила пустота.
   *
   * Ни типы, ни сборка этого не видели: ответ службы был описан здесь
   * же, руками, и описан неверно. Не видела и сверка: у заглушки пробы
   * стоял тот же выдуманный ключ `campaigns`, и на снимке всё сходилось.
   * Вылезло 26.08, когда заглушку привели к засеву службы.
   *
   * `campaigns` оставлен запасным: если тариф всё-таки придёт со старым
   * ключом, плашка покажет число, а не пустоту.
   * ═══════════════════════════════════════════════════════════════════
   */
  const занято = q.data.current.usage ?? null;
  return {
    cabinets: потолки['cabinets'] ?? null,
    campaigns: потолки['managed_campaigns'] ?? потолки['campaigns'] ?? null,
    занятоКабинетов: занято?.['cabinets'] ?? null,
    занятоКампаний: занято?.['managed_campaigns'] ?? null,
    тесты: q.data.current.tests ?? null,
  };
}

export function usePlanLine(wsId: string | undefined): string | undefined {
  const q = useQuery({
    queryKey: ['billing', wsId],
    queryFn: () =>
      api<{
        current: {
          planName: string | null;
          health: 'active' | 'ending' | 'expired';
          frozen: boolean;
          expiresAt: string | null;
        } | null;
      }>(`/workspaces/${wsId}/billing`),
    enabled: Boolean(wsId),
    retry: false,
    staleTime: 5 * 60_000,
  });

  // Не прочиталось — молчим: подпись не то сведение, ради которого
  // стоит ругаться в меню
  if (q.error || q.data === undefined) return undefined;
  return подписьТарифа(q.data.current);
}

export function useSubscriptionHint(wsId: string | undefined): string | null {
  const q = useQuery({
    queryKey: ['billing', wsId],
    queryFn: () =>
      api<{
        current: { health: 'active' | 'ending' | 'expired'; daysLeft: number | null; frozen: boolean } | null;
      }>(`/workspaces/${wsId}/billing`),
    enabled: Boolean(wsId),
    retry: false,
    // Срок меняется раз в сутки: спрашивать чаще нечего
    staleTime: 5 * 60_000,
  });

  if (q.error) return null;
  if (q.data === undefined) return null;

  return planHint(q.data.current);
}
